/**
 * OrderConfirmationPdf — klant-orderbevestiging (epic 2, FR-4.3/4.4/4.5).
 * Toont klantgegevens, pakketinhoud (artikelen + aantallen) en de eindprijs (verkoop incl. btw).
 * Bevat GEEN leveranciersinfo, inkoopprijzen of marges — gerenderd uit de bevroren snapshot.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { formatDateNL } from '@/lib/pdf-brand';
import type { ConfirmationSnapshot } from '@/lib/orderConfirmation';

const C = {
  primary: '#1E2D3D',
  accent: '#E87C3E',
  text: '#1E2D3D',
  muted: '#64748B',
  divider: '#E2E8F0',
  bg: '#FFFFFF',
};

const styles = StyleSheet.create({
  page: { backgroundColor: C.bg, color: C.text, padding: 40, fontSize: 11 },
  h1: { fontSize: 20, fontWeight: 700, color: C.primary, marginBottom: 2 },
  sub: { fontSize: 10, color: C.muted, marginBottom: 16 },
  clientBox: { marginBottom: 20 },
  clientName: { fontSize: 13, fontWeight: 700 },
  pkg: { marginBottom: 16 },
  pkgTitle: { fontSize: 13, fontWeight: 700, color: C.accent, marginBottom: 6 },
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3, borderBottomWidth: 1, borderBottomColor: C.divider },
  pkgTotal: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6, fontWeight: 700 },
  grandTotal: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 18, paddingTop: 10, borderTopWidth: 2, borderTopColor: C.primary, fontSize: 14, fontWeight: 700 },
  footer: { position: 'absolute', bottom: 28, left: 40, right: 40, fontSize: 9, color: C.muted, textAlign: 'center' },
});

function eur(n: number): string {
  return `EUR ${n.toFixed(2)}`;
}

interface Props {
  snapshot: ConfirmationSnapshot;
  version: number;
  date: string;
}

export function OrderConfirmationPdf({ snapshot, version, date }: Props) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.h1}>Orderbevestiging</Text>
        <Text style={styles.sub}>
          Versie {version} · {formatDateNL(date)}
        </Text>

        <View style={styles.clientBox}>
          <Text style={styles.clientName}>{snapshot.client.company_name}</Text>
          {snapshot.client.contact_name ? <Text>{snapshot.client.contact_name}</Text> : null}
          {snapshot.client.address ? <Text>{snapshot.client.address}</Text> : null}
        </View>

        {snapshot.packages.map((pkg, i) => (
          <View key={i} style={styles.pkg} wrap={false}>
            <Text style={styles.pkgTitle}>{pkg.name}</Text>
            {pkg.items.map((it, j) => (
              <View key={j} style={styles.row}>
                <Text>{it.name}</Text>
                <Text>{it.quantity}x</Text>
              </View>
            ))}
            <View style={styles.pkgTotal}>
              <Text>Eindprijs (incl. btw)</Text>
              <Text>{eur(pkg.eindprijs)}</Text>
            </View>
          </View>
        ))}

        <View style={styles.grandTotal}>
          <Text>Totaal</Text>
          <Text>{eur(snapshot.eindprijs_totaal)}</Text>
        </View>

        <Text style={styles.footer}>2BMade · Orderbevestiging v{version}</Text>
      </Page>
    </Document>
  );
}
